import { Link } from "@tanstack/react-router";
import { useCallback, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useEntitiesMeta } from "./api";
import type { EntitySummary } from "./types";

export type RecordTarget =
  | { kind: "section"; workspace: string; section: string; id: string }
  | { kind: "generic"; entity: string; id: string };

export function recordTarget(summary: EntitySummary | undefined, entity: string, id: string): RecordTarget {
  if (summary?.workspace && summary.section) {
    return { kind: "section", workspace: summary.workspace, section: summary.section, id };
  }
  return { kind: "generic", entity, id };
}

export function targetHref(t: RecordTarget): string {
  return t.kind === "section" ? `/${t.workspace}/${t.section}/${t.id}` : `/records/${t.entity}/${t.id}`;
}

/**
 * Returns a resolver for links to records of any entity. `null` when the caller
 * can't read the target entity (no link is rendered then).
 */
export function useRecordLinker() {
  const { data: entities } = useEntitiesMeta();
  return useCallback(
    (entity: string, id: string): RecordTarget | null => {
      if (!entities) return null;
      const summary = entities.find((e) => e.name === entity);
      if (!summary) return null;
      return recordTarget(summary, entity, id);
    },
    [entities],
  );
}

export function RecordLink({
  target,
  children,
  className,
}: {
  target: RecordTarget;
  children: ReactNode;
  className?: string;
}) {
  const cls = cn(
    "rounded-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:outline-none",
    className,
  );
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  if (target.kind === "section") {
    return (
      <Link
        to="/$workspace/$section/$id"
        params={{ workspace: target.workspace, section: target.section, id: target.id }}
        className={cls}
        onClick={stop}
      >
        {children}
      </Link>
    );
  }
  return (
    <Link to="/records/$entity/$id" params={{ entity: target.entity, id: target.id }} className={cls} onClick={stop}>
      {children}
    </Link>
  );
}
